/**
 * OnboardingWizard v3.0 — 8-step brand profile interview
 * Steps: Welcome → Business → Topics (Primary/Secondary + Split) → Tone & Style →
 *        Website Scrape → Brand Rules → Writing Samples → Done
 *
 * New: multi-select tone/style, primary+secondary topics with split %, file uploads for writing samples
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Brain, Globe, Target, Sparkles, ChevronRight, ChevronLeft,
  X, Check, RefreshCw, Plus, Zap, Shield, FileText, Upload,
  BookOpen, Sliders, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TONES = ['Professional', 'Conversational', 'Authoritative', 'Inspirational', 'Bold', 'Educational', 'Thought-Provoking', 'Analytical', 'Empowering', 'Direct'];
const STYLES = ['Authoritative', 'Conversational', 'Visionary', 'Investigative', 'Thought-Provoking', 'Scientific', 'Predictive', 'Storytelling', 'Data-Driven', 'Narrative'];
const PLATFORMS_ALL = ['LinkedIn', 'Twitter/X', 'Instagram', 'TikTok', 'Facebook', 'YouTube', 'Newsletter', 'Blog/Article'];
const DEFAULT_PRIMARY = ['AI & Cybersecurity', 'Generative AI', 'Digital Transformation', 'Future of Work', 'Machine Learning', 'SaaS & Product', 'Tech Startups', 'Cloud Computing', 'B2B Sales', 'Leadership & Management', 'Data Science', 'Blockchain & Web3', 'AI Marketing', 'Product Management', 'Threat Intelligence'];

const STEPS = [
  { id: 'welcome',   label: 'Welcome',        icon: Brain },
  { id: 'business',  label: 'Your Business',  icon: Target },
  { id: 'topics',    label: 'Topics',         icon: Sliders },
  { id: 'tone',      label: 'Voice & Tone',   icon: Sparkles },
  { id: 'website',   label: 'Website',        icon: Globe },
  { id: 'brand',     label: 'Brand Rules',    icon: Shield },
  { id: 'samples',   label: 'Writing Samples', icon: FileText },
  { id: 'done',      label: 'Ready',          icon: Check },
];

// Small multi-select toggle chip
function Chip({ label, selected, onClick }) {
  return (
    <button onClick={onClick}
      className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all select-none"
      style={{
        background: selected ? 'rgba(124,94,250,0.2)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${selected ? 'rgba(124,94,250,0.6)' : 'rgba(255,255,255,0.1)'}`,
        color: selected ? '#a68eff' : 'rgba(255,255,255,0.45)',
      }}>
      {selected && <Check size={8} className="inline mr-1" />}
      {label}
    </button>
  );
}

export function OnboardingWizard({ onComplete, editMode = false }) {
  const [step, setStep] = useState(editMode ? 1 : 0);
  const [profile, setProfile] = useState({
    business_name: '', website_url: '', description: '',
    target_audience: '', unique_value: '', positioning: '',
    // Topics
    primary_topics: [], secondary_topics: [], focus_topics: [],
    topic_split_primary: 80,   // %
    // Tone & Voice (arrays for multi-select)
    tones: ['Professional'], styles: ['Authoritative'],
    tone: 'Professional', style: 'Authoritative',
    preferred_platforms: ['LinkedIn'],
    // Brand Rules
    writing_rules: '', guardrails: '',
    cta_type: 'website', cta_url: '', cta_label: '',
    brand_voice_examples: '',
    // Writing samples metadata (text stored in DB separately)
    writing_samples: [],
  });

  const [newPrimary, setNewPrimary] = useState('');
  const [newSecondary, setNewSecondary] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileRef = useRef(null);

  const totalSteps = STEPS.length;

  useEffect(() => {
    if (editMode) {
      axios.get(`${API}/api/onboarding/profile`).then(({ data }) => {
        if (data.profile) {
          const p = data.profile;
          setProfile(prev => ({
            ...prev, ...p,
            tones: p.tones || (p.tone ? [p.tone] : ['Professional']),
            styles: p.styles || (p.style ? [p.style] : ['Authoritative']),
            primary_topics: p.primary_topics || p.focus_topics || [],
            secondary_topics: p.secondary_topics || [],
            preferred_platforms: p.preferred_platforms || ['LinkedIn'],
          }));
        }
      }).catch(() => {});
    }
  }, [editMode]);

  const update = (key, val) => setProfile(p => ({ ...p, [key]: val }));

  // Multi-select toggle helper
  const toggleMulti = (key, val, maxCount = 5) => {
    setProfile(p => {
      const arr = p[key] || [];
      if (arr.includes(val)) return { ...p, [key]: arr.filter(v => v !== val) };
      if (arr.length >= maxCount) return p;
      return { ...p, [key]: [...arr, val] };
    });
  };

  const addPrimary = () => {
    if (!newPrimary.trim()) return;
    setProfile(p => ({ ...p, primary_topics: [...(p.primary_topics || []), newPrimary.trim()], focus_topics: [...(p.focus_topics || []), newPrimary.trim()] }));
    setNewPrimary('');
  };

  const addSecondary = () => {
    if (!newSecondary.trim()) return;
    setProfile(p => ({ ...p, secondary_topics: [...(p.secondary_topics || []), newSecondary.trim()] }));
    setNewSecondary('');
  };

  const scrapeWebsite = async () => {
    if (!profile.website_url) return;
    setScraping(true);
    setScrapeResult('');
    try {
      const { data } = await axios.post(`${API}/api/onboarding/scrape-website`, { url: profile.website_url });
      if (data.success && data.summary) {
        setScrapeResult(data.summary);
        update('description', data.summary.slice(0, 400));
        if (data.detected_topics?.length > 0) {
          setProfile(p => ({
            ...p,
            primary_topics: [...new Set([...(p.primary_topics || []), ...data.detected_topics.slice(0, 3)])],
            focus_topics: [...new Set([...(p.focus_topics || []), ...data.detected_topics.slice(0, 3)])],
          }));
        }
      }
    } catch {
      setScrapeResult('Could not scrape website. You can still continue — add your description manually.');
    }
    setScraping(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/onboarding/upload-sample`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setProfile(p => ({
          ...p,
          writing_samples: [...(p.writing_samples || []), {
            filename: file.name,
            preview: data.preview || '',
            word_count: data.word_count || 0,
          }],
        }));
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'File upload failed. Try PDF, DOCX, or TXT.');
    }
    setUploadingFile(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const save = async () => {
    setSaving(true);
    try {
      // Sync single-value fields from arrays
      const saveProfile = {
        ...profile,
        tone: (profile.tones || ['Professional'])[0],
        style: (profile.styles || ['Authoritative'])[0],
        focus_topics: [...new Set([...(profile.primary_topics || []), ...(profile.secondary_topics || [])])],
        completed: true,
      };
      await axios.post(`${API}/api/onboarding/profile`, saveProfile);
      if (editMode) {
        onComplete?.();
      } else {
        setStep(7); // Done
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save profile. Please try again.');
    }
    setSaving(false);
  };

  const finish = () => {
    localStorage.setItem('ideaworks_onboarding_done', '1');
    onComplete?.();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(16px)' }}>
      <div className="w-full max-w-lg rounded-3xl overflow-hidden flex flex-col"
        style={{ background: '#080810', border: '1px solid rgba(124,94,250,0.25)', boxShadow: '0 30px 80px rgba(0,0,0,0.7)', maxHeight: '90vh' }}>

        {/* Progress bar */}
        <div className="flex items-center gap-0 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <div key={s.id} className="flex-1 flex flex-col items-center py-2.5 gap-1 transition-all"
                style={{
                  background: active ? 'rgba(124,94,250,0.08)' : 'transparent',
                  borderBottom: active ? '2px solid #7c5efa' : '2px solid transparent',
                }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: done ? '#3dffa0' : active ? '#7c5efa' : 'rgba(255,255,255,0.08)' }}>
                  {done ? <Check size={10} color="#000" /> : <Icon size={9} color={active ? '#fff' : 'rgba(255,255,255,0.3)'} />}
                </div>
                <span className="text-[8px] font-bold hidden sm:block"
                  style={{ color: active ? '#a68eff' : done ? '#3dffa0' : 'rgba(255,255,255,0.25)' }}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">

          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center py-4 space-y-5">
              <div className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(124,94,250,0.3), rgba(166,142,255,0.1))', border: '1px solid rgba(124,94,250,0.4)' }}>
                <Brain size={36} style={{ color: '#a68eff' }} />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white mb-3">Meet Einstein.</h2>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Your AI creative strategist. I need 3 minutes to understand your business — then I'll generate, plan, and optimize every piece of content for you, on autopilot.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-left">
                {['Tailored topics & angles', 'Your tone in every word', '5-day plans every morning', 'Multi-channel publishing'].map(f => (
                  <div key={f} className="rounded-xl p-3 flex items-center gap-2"
                    style={{ background: 'rgba(124,94,250,0.07)', border: '1px solid rgba(124,94,250,0.15)' }}>
                    <Zap size={11} style={{ color: '#a68eff', flexShrink: 0 }} />
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Business */}
          {step === 1 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Tell me about your business</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>This becomes the foundation for every piece of content I create.</p>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Business / Brand Name *</label>
                <input value={profile.business_name} onChange={e => update('business_name', e.target.value)}
                  data-testid="wizard-business-name"
                  placeholder="e.g. IconIQs.AI or Your Company Name"
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>What does your business do?</label>
                <textarea value={profile.description} onChange={e => update('description', e.target.value)}
                  placeholder="Describe your products, services, and the problem you solve in 2-3 sentences."
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Target Audience</label>
                  <input value={profile.target_audience} onChange={e => update('target_audience', e.target.value)}
                    placeholder="e.g. CISOs, tech founders"
                    className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Unique Value</label>
                  <input value={profile.unique_value} onChange={e => update('unique_value', e.target.value)}
                    placeholder="What makes you different?"
                    className="w-full px-3 py-2 rounded-xl text-sm outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Topics (Primary + Secondary + Split) */}
          {step === 2 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Content Topics</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Primary topics drive 80% of your content. Secondary topics add context and reach.</p>
              </div>

              {/* Primary Topics */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: '#a68eff' }}>
                  Primary Topics — {profile.topic_split_primary || 80}% of content
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {DEFAULT_PRIMARY.map(t => (
                    <Chip key={t} label={t} selected={(profile.primary_topics || []).includes(t)}
                      onClick={() => toggleMulti('primary_topics', t, 8)} />
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={newPrimary} onChange={e => setNewPrimary(e.target.value)}
                    placeholder="Add custom primary topic..."
                    className="flex-1 px-3 py-1.5 rounded-xl text-xs outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e8e8f0' }}
                    onKeyDown={e => e.key === 'Enter' && addPrimary()} />
                  <button onClick={addPrimary}
                    className="px-3 py-1.5 rounded-xl text-xs font-bold"
                    style={{ background: 'rgba(124,94,250,0.15)', color: '#a68eff', border: '1px solid rgba(124,94,250,0.3)' }}>
                    <Plus size={11} />
                  </button>
                </div>
                {(profile.primary_topics || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {profile.primary_topics.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ background: 'rgba(124,94,250,0.2)', color: '#a68eff', border: '1px solid rgba(124,94,250,0.4)' }}>
                        {t}
                        <button onClick={() => setProfile(p => ({ ...p, primary_topics: p.primary_topics.filter((_, j) => j !== i) }))}>
                          <X size={8} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Topic Split Slider */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Topic Split: {profile.topic_split_primary || 80}% Primary / {100 - (profile.topic_split_primary || 80)}% Secondary
                </label>
                <input type="range" min={50} max={95} step={5}
                  value={profile.topic_split_primary || 80}
                  onChange={e => update('topic_split_primary', Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: '#7c5efa' }} />
                <div className="flex justify-between text-[9px] mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  <span>50% / 50%</span><span>80% / 20%</span><span>95% / 5%</span>
                </div>
              </div>

              {/* Secondary Topics */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: '#3dffa0' }}>
                  Secondary Topics — {100 - (profile.topic_split_primary || 80)}% of content
                </label>
                <div className="flex gap-2">
                  <input value={newSecondary} onChange={e => setNewSecondary(e.target.value)}
                    placeholder="e.g. Business Strategy, Mental Health in Tech..."
                    className="flex-1 px-3 py-1.5 rounded-xl text-xs outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e8e8f0' }}
                    onKeyDown={e => e.key === 'Enter' && addSecondary()} />
                  <button onClick={addSecondary}
                    className="px-3 py-1.5 rounded-xl text-xs font-bold"
                    style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.3)' }}>
                    <Plus size={11} />
                  </button>
                </div>
                {(profile.secondary_topics || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {profile.secondary_topics.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{ background: 'rgba(61,255,160,0.12)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.3)' }}>
                        {t}
                        <button onClick={() => setProfile(p => ({ ...p, secondary_topics: p.secondary_topics.filter((_, j) => j !== i) }))}>
                          <X size={8} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Tone & Voice (multi-select) */}
          {step === 3 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Voice & Tone</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Pick up to 3 tones. Einstein will blend these to match your brand voice across all content.</p>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: '#a68eff' }}>
                  Tone (pick up to 3)
                  <span className="ml-2 normal-case font-normal" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {(profile.tones || []).length}/3 selected
                  </span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {TONES.map(t => (
                    <Chip key={t} label={t} selected={(profile.tones || []).includes(t)}
                      onClick={() => toggleMulti('tones', t, 3)} />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: '#60a5fa' }}>
                  Writing Style (pick up to 2)
                  <span className="ml-2 normal-case font-normal" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    {(profile.styles || []).length}/2 selected
                  </span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {STYLES.map(s => (
                    <Chip key={s} label={s} selected={(profile.styles || []).includes(s)}
                      onClick={() => toggleMulti('styles', s, 2)} />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: '#f97316' }}>
                  Preferred Platforms (pick all that apply)
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {PLATFORMS_ALL.map(p => (
                    <Chip key={p} label={p} selected={(profile.preferred_platforms || []).includes(p)}
                      onClick={() => toggleMulti('preferred_platforms', p, 8)} />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Brand Voice Examples (optional)</label>
                <textarea value={profile.brand_voice_examples} onChange={e => update('brand_voice_examples', e.target.value)}
                  placeholder="Paste 1-2 example sentences or hooks that represent your voice..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>
            </div>
          )}

          {/* Step 4: Website Scrape */}
          {step === 4 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Your Website</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Einstein will read your website to understand your business better and use your URL as the content CTA.</p>
              </div>
              <div className="flex gap-2">
                <input value={profile.website_url} onChange={e => update('website_url', e.target.value)}
                  data-testid="wizard-website-url"
                  placeholder="https://yourwebsite.com"
                  className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                <button onClick={scrapeWebsite} disabled={!profile.website_url || scraping}
                  data-testid="wizard-scrape-btn"
                  className="px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: 'rgba(124,94,250,0.15)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
                  {scraping ? <RefreshCw size={12} className="animate-spin" /> : <Globe size={12} />}
                  {scraping ? 'Scanning...' : 'Read Site'}
                </button>
              </div>
              {scrapeResult && (
                <div className="rounded-xl p-3" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)' }}>
                  <div className="text-[10px] font-bold uppercase mb-1" style={{ color: '#3dffa0' }}>Site Summary (AI-generated)</div>
                  <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{scrapeResult.slice(0, 300)}...</div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Brand Rules */}
          {step === 5 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Brand Rules & CTA</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Einstein uses these as hard guardrails. Every piece of content checks against these rules.</p>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Call-To-Action Preference</label>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {[{ val: 'website', label: 'Visit Website', sub: 'Link to your site' }, { val: 'consultation', label: 'Book Consultation', sub: 'Booking / calendar link' }].map(opt => (
                    <button key={opt.val} onClick={() => update('cta_type', opt.val)}
                      className="p-3 rounded-xl text-left transition-all"
                      style={{
                        background: profile.cta_type === opt.val ? 'rgba(124,94,250,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${profile.cta_type === opt.val ? 'rgba(124,94,250,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      }}>
                      <div className="text-xs font-bold" style={{ color: profile.cta_type === opt.val ? '#a68eff' : 'rgba(255,255,255,0.6)' }}>{opt.label}</div>
                      <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{opt.sub}</div>
                    </button>
                  ))}
                </div>
                <input value={profile.cta_url} onChange={e => update('cta_url', e.target.value)}
                  placeholder={profile.cta_type === 'consultation' ? 'https://calendly.com/you' : 'https://yourwebsite.com'}
                  data-testid="wizard-cta-url"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none mb-2"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
                <input value={profile.cta_label} onChange={e => update('cta_label', e.target.value)}
                  placeholder={`e.g. "Learn more" or "Book a free 30-min call"`}
                  data-testid="wizard-cta-label"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Content Guardrails</label>
                <textarea value={profile.guardrails} onChange={e => update('guardrails', e.target.value)}
                  placeholder="e.g. Never use fear-based messaging. Always promote AI as empowering. Don't mention competitor X."
                  rows={2}
                  data-testid="wizard-guardrails"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider block mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Writing Rules</label>
                <textarea value={profile.writing_rules} onChange={e => update('writing_rules', e.target.value)}
                  placeholder="e.g. Use short punchy sentences. Lead with the insight. Always end with a question."
                  rows={2}
                  data-testid="wizard-writing-rules"
                  className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8e8f0' }} />
              </div>
            </div>
          )}

          {/* Step 6: Writing Samples */}
          {step === 6 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-base font-black text-white mb-1">Writing Samples</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Upload your best articles, posts, or any writing. Einstein will learn your voice, vocabulary, and style from them. Accepts PDF, DOCX, TXT.</p>
              </div>

              {/* Upload button */}
              <div
                onClick={() => fileRef.current?.click()}
                className="w-full py-8 rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer transition-all hover:opacity-80"
                style={{ background: 'rgba(96,165,250,0.05)', border: '2px dashed rgba(96,165,250,0.25)' }}>
                {uploadingFile ? (
                  <RefreshCw size={24} className="animate-spin" style={{ color: '#60a5fa' }} />
                ) : (
                  <Upload size={24} style={{ color: '#60a5fa' }} />
                )}
                <div className="text-sm font-semibold" style={{ color: '#60a5fa' }}>
                  {uploadingFile ? 'Processing...' : 'Click to upload writing sample'}
                </div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>PDF · DOCX · TXT — max 10 MB</div>
                <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.doc" className="hidden" onChange={handleFileUpload} />
              </div>

              {/* Uploaded samples list */}
              {(profile.writing_samples || []).length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {profile.writing_samples.length} sample{profile.writing_samples.length > 1 ? 's' : ''} uploaded
                  </div>
                  {profile.writing_samples.map((sample, i) => (
                    <div key={i} className="rounded-xl p-3 flex items-start gap-3"
                      style={{ background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.2)' }}>
                      <BookOpen size={14} style={{ color: '#60a5fa', flexShrink: 0, marginTop: 1 }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-white truncate">{sample.filename}</div>
                        <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          {sample.word_count} words · {sample.preview?.slice(0, 80)}...
                        </div>
                      </div>
                      <button onClick={() => setProfile(p => ({ ...p, writing_samples: p.writing_samples.filter((_, j) => j !== i) }))}
                        className="flex-shrink-0 p-1 rounded-lg hover:opacity-70"
                        style={{ color: 'rgba(255,255,255,0.3)' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(profile.writing_samples || []).length === 0 && (
                <div className="text-center py-2">
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    Writing samples are optional but highly recommended. Einstein learns your unique voice from them.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 7: Done */}
          {step === 7 && (
            <div className="text-center py-6 space-y-5">
              <div className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center"
                style={{ background: 'rgba(61,255,160,0.12)', border: '1px solid rgba(61,255,160,0.3)' }}>
                <Check size={40} style={{ color: '#3dffa0' }} />
              </div>
              <div>
                <h3 className="text-xl font-black text-white mb-2">Einstein is ready!</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Profile saved for <span className="text-white font-semibold">{profile.business_name || 'your business'}</span>. Every piece of content will reflect your tone, topics, guardrails, and CTA.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {['Auto Strategy', 'Blog Publisher', 'Einstein Advisory', 'Ad Video Agent', 'Content Studio', 'Morning Plans'].map(f => (
                  <div key={f} className="rounded-xl p-2.5 flex items-center gap-2"
                    style={{ background: 'rgba(61,255,160,0.05)', border: '1px solid rgba(61,255,160,0.15)' }}>
                    <Check size={10} style={{ color: '#3dffa0' }} />
                    <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between px-8 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {step > 0 && step < 7 ? (
            <button onClick={() => setStep(s => s - 1)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
              <ChevronLeft size={16} /> Back
            </button>
          ) : <div />}

          {step < 5 && (
            <button onClick={() => setStep(s => s + 1)} data-testid="wizard-next-btn"
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              {step === 0 ? "Let's Start" : 'Continue'} <ChevronRight size={16} />
            </button>
          )}

          {step === 5 && (
            <button onClick={() => setStep(6)} data-testid="wizard-next-btn"
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              Continue <ChevronRight size={16} />
            </button>
          )}

          {step === 6 && (
            <button onClick={save} disabled={saving} data-testid="wizard-save-btn"
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #7c5efa, #a68eff)', color: '#fff' }}>
              {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : <><Check size={14} /> Save Profile</>}
            </button>
          )}

          {step === 7 && (
            <button onClick={finish} data-testid="wizard-finish-btn"
              className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: 'linear-gradient(135deg, #3dffa0, #00d4b8)', color: '#0a0a12' }}>
              <Zap size={14} /> Start Creating
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
